import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import { authenticationResult } from '../../auth/store/auth.reducer';
import { modalService } from '../../modal/modal.service';
import { ApiError } from '../../models/api-error.enum';
import type { AppDispatch, RootState } from '../../store';
import { toastService } from '../../toast/toast.service';
import { MembershipConfirmTermsModal } from '../modals/membership-confirm-terms.modal';
import type { ConfirmTermsResult } from '../models/membership.model';
import { MembershipJoinFlowService } from '../services/membership-join-flow.service';
import { setJoinInProgress, setMembershipJoinRequest } from './membership.reducer';
import {
  addMembershipToCart,
  createContract,
  loadMemberships,
  updateMembership,
} from './membership.thunk';

export const membershipListener = createListenerMiddleware();

/**
 * Listener 1: Process stored request after memberships load
 *
 * Replaces: processStoredMembershipJoinRequest$ effect (NgRx)
 *
 * When memberships finish loading, check if there's a stored
 * join request in localStorage and process it.
 */
membershipListener.startListening({
  matcher: isAnyOf(loadMemberships.fulfilled),
  effect: async (_action, listenerApi) => {
    const dispatch = listenerApi.dispatch as AppDispatch;
    const state = listenerApi.getState() as RootState;

    // Only process stored request when authenticated — if we clear localStorage while the
    // user is being redirected to Shopify login, Listener 2 will find nothing on return.
    if (state.auth.loggedIn) {
      MembershipJoinFlowService.processStoredMembershipJoinRequest(dispatch);
    }
  },
});

/**
 * Open legal terms modal using modal service
 * Returns a Promise that resolves with user's choice
 */
const openLegalTermsModal = (terms: string): Promise<ConfirmTermsResult> => {
  return new Promise((resolve, reject) => {
    modalService.open({
      component: MembershipConfirmTermsModal,
      props: {
        legalTerms: terms,
        onClose: (result: ConfirmTermsResult) => {
          modalService.closeAll();
          if (result.termsAccepted) {
            resolve(result);
          } else {
            reject(result);
          }
        },
      },
      maxWidth: 515,
    });
  });
};

/**
 * Listener 2: Process stored request after authentication
 *
 * Replaces: processStoredMembershipJoinRequestAfterAuthentication$ effect (NgRx)
 *
 * When authentication succeeds (and doesn't redirect), check if
 * there's a stored join request and process it.
 */
membershipListener.startListening({
  actionCreator: authenticationResult,
  effect: async (action, listenerApi) => {
    const dispatch = listenerApi.dispatch as AppDispatch;
    const result = action.payload;
    if (result?.success && !result?.loadUrl) {
      // Consume (once per page load) WITHOUT clearing localStorage — the stored
      // request must survive a mid-flow refresh (SNGL-6967). Do NOT dispatch
      // setMembershipJoinRequest, as that would trigger Listener 3 and cause a
      // duplicate processMembershipJoin call.
      const storedRequest = MembershipJoinFlowService.consumeStoredMembershipJoinRequest();

      let memberships = (listenerApi.getState() as RootState).membership.memberships.value;

      // Auth has succeeded — safe to load memberships without an isAuthenticated guard.
      if (storedRequest && !memberships) {
        dispatch(setJoinInProgress(true));
        await dispatch(loadMemberships());
        memberships = (listenerApi.getState() as RootState).membership.memberships.value;
      }

      if (storedRequest && memberships) {
        await MembershipJoinFlowService.processMembershipJoin(
          result,
          storedRequest,
          memberships,
          dispatch,
          openLegalTermsModal
        );
      } else if (storedRequest) {
        // Memberships failed to load — don't leave gated components in a
        // permanent waiting state; the stored request survives for a retry.
        dispatch(setJoinInProgress(false));
      }
    }
  },
});

/**
 * Listener 3: Load memberships if not loaded when join request is set
 *
 * Replaces: processMembershipLoadOnJoinRequest$ effect (NgRx)
 *
 * When a join request is set, ensure memberships are loaded.
 * If not loaded, trigger load. If already loaded and user is authenticated,
 * process the join immediately.
 */
membershipListener.startListening({
  actionCreator: setMembershipJoinRequest,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const dispatch = listenerApi.dispatch as AppDispatch;

    const memberships = state.membership.memberships.value;
    const request = action.payload;
    const isAuthenticated = state.auth.loggedIn;

    // Authenticated join underway — let gated components show a waiting state
    // instead of the access lock while memberships load (SNGL-6967).
    if (request && isAuthenticated) {
      dispatch(setJoinInProgress(true));
    }

    // Load memberships if not already loaded
    if (request && (!memberships || memberships.length === 0)) {
      await dispatch(loadMemberships());
      // After memberships load, the loadMemberships.fulfilled listener will NOT call
      // processMembershipJoin, so we need to call it here
      const updatedState = listenerApi.getState() as RootState;
      const loadedMemberships = updatedState.membership.memberships.value;
      if (loadedMemberships && (listenerApi.getState() as RootState).auth.loggedIn) {
        await MembershipJoinFlowService.processMembershipJoin(
          {
            success: true,
            termsAccepted: false,
            optInToMarketingEmails: false,
          },
          request,
          loadedMemberships,
          dispatch,
          openLegalTermsModal
        );
      } else {
        // Memberships failed to load — don't leave gated components waiting;
        // the stored request survives for a retry on the next page load.
        dispatch(setJoinInProgress(false));
      }
    } else if (memberships && isAuthenticated && request) {
      // Memberships already loaded and user is authenticated, process join immediately
      await MembershipJoinFlowService.processMembershipJoin(
        {
          success: true,
          termsAccepted: false,
          optInToMarketingEmails: false,
        },
        request,
        memberships,
        dispatch,
        openLegalTermsModal
      );
    }
  },
});

membershipListener.startListening({
  actionCreator: addMembershipToCart.fulfilled,
  effect: async () => {
    // Terminal outcome — clear before navigating so returning from checkout
    // doesn't re-fire the join.
    MembershipJoinFlowService.clearStoredMembershipJoinRequest();
    window.location.href = '/checkout';
  },
});

membershipListener.startListening({
  actionCreator: addMembershipToCart.rejected,
  effect: async (action, listenerApi) => {
    const dispatch = listenerApi.dispatch as AppDispatch;
    MembershipJoinFlowService.clearStoredMembershipJoinRequest();
    dispatch(setJoinInProgress(false));
    const message = action.payload?.message || 'Unable to add product to cart.';
    toastService.error('Add to Cart Error', message);
  },
});

membershipListener.startListening({
  actionCreator: createContract.fulfilled,
  effect: async (action, listenerApi) => {
    const dispatch = listenerApi.dispatch as AppDispatch;

    // Clear any stored join request to prevent redirect loops
    MembershipJoinFlowService.clearStoredMembershipJoinRequest();

    // Reload memberships to get the newly created contract
    await dispatch(loadMemberships());

    // Redirect after memberships are loaded
    if (action.payload.redirectUrl) {
      window.location.href = action.payload.redirectUrl;
    } else {
      window.location.href = `${window.location.origin}/apps/single/membership`;
    }
  },
});

membershipListener.startListening({
  actionCreator: createContract.rejected,
  effect: async (action, listenerApi) => {
    const dispatch = listenerApi.dispatch as AppDispatch;

    // The app proxy reported no customer for this request, so the contract was
    // never attempted. Keep the stored request so a later boot that does have a
    // session can replay it (SNGL-7101).
    if (action.payload === ApiError.Session_Not_Ready) {
      dispatch(setJoinInProgress(false));
      return;
    }

    MembershipJoinFlowService.clearStoredMembershipJoinRequest();

    // 409 = a contract already exists for this customer + membership (e.g. the
    // client-side tier check missed it, or the membership data was stale). The
    // fan is already a member — proceed exactly like a successful join instead
    // of stranding them at the access lock with an error toast (SNGL-6967).
    if (action.payload === ApiError.Conflict) {
      await dispatch(loadMemberships());
      const redirectUrl = action.meta.arg.redirectUrl;
      window.location.href = redirectUrl || `${window.location.origin}/apps/single/membership`;
      return;
    }

    dispatch(setJoinInProgress(false));
    toastService.error('Error', 'Unable to create membership contract');
  },
});
